// Pestaña "Medición" (cerebro, etapa 0): reglas de "qué mostrar", sin React, para poder
// probarlas sueltas (scripts/brain-view.test.ts). Nunca se muestra un error interno ni un
// nombre interno: ante la duda, un texto fijo en español u "Otro".

export const SUMMARY_FAIL =
  'No se pudo leer la medición. Se vuelve a intentar sola en unos segundos.';
export const SWITCH_FAIL = 'No se pudo cambiar el interruptor. Probá de nuevo en un momento.';
export const LIST_FAIL_KEPT =
  'No se pudo leer la lista de asuntos. Se muestra la última que se pudo leer.';
export const LIST_FAIL_NEVER = 'No se pudo leer la lista de asuntos.';
export const OTHER = 'Otro';
/** Encima de la nota de cada asunto: en esta etapa el análisis es solo una prueba. */
export const DRAFT_NOTE = 'Borrador de prueba del análisis. No se envió nada ni se cambió nada.';
/** Debajo del indicador "Asuntos fuera de plazo": con el tope diario es normal que haya espera. */
export const LATE_NOTE =
  'Con el tope de análisis por día es esperable que haya asuntos esperando. Lo urgente se atiende primero.';

/**
 * Textos del servidor que se pueden mostrar tal cual (vienen de las rutas de la medición, en
 * español). Otras capas del servidor responden en inglés ("forbidden", "Authentication
 * required", "Session expired"): esos nunca se muestran, se usa el texto fijo.
 */
const SERVER_TEXTS = new Set([
  'Solo el administrador puede usar esta pantalla.',
  'El análisis automático todavía no está disponible: falta el próximo reinicio del sistema.',
  'No se pudo leer la medición.',
  'No se pudo leer la lista de asuntos.',
  'No se pudo cambiar el interruptor.',
  // Pestaña "Decisiones" (etapa 1).
  'No encontré esa propuesta del cerebro.',
  'Esa propuesta ya fue decidida o se anuló.',
  'Ya la respondieron por otra vía: no se envió nada.',
  'Ya le respondieron al comprador por otra vía: no se envió nada.',
  'No se pudo verificar en Mercado Libre si sigue abierta. No se envió nada; probá en un momento.',
  'Motivo inválido.',
  'El texto editado está vacío.',
  'No se pudo leer la lista de decisiones.',
  'No se pudo aprobar la propuesta.',
  'No se pudo rechazar la propuesta.',
]);

/** Resultado de un pedido a la API, ya clasificado. */
export type Fetched<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'server_error'; error?: string }
  | { kind: 'exception' };

/** Hace el pedido y lo clasifica; una excepción (red caída, página de error) no se propaga. */
export async function fetchResult<T>(
  call: () => Promise<{ success: boolean; data?: T; error?: string }>
): Promise<Fetched<T>> {
  try {
    const r = await call();
    if (r && r.success && r.data !== undefined) return { kind: 'ok', data: r.data };
    return { kind: 'server_error', error: r?.error };
  } catch {
    return { kind: 'exception' };
  }
}

/** Texto a mostrar para un fallo: el del servidor solo si es uno conocido en español. */
export function failureText(f: Fetched<unknown>, fallback: string): string | null {
  if (f.kind === 'ok') return null;
  if (f.kind === 'server_error' && f.error && SERVER_TEXTS.has(f.error)) return f.error;
  return fallback;
}

/** Tras un refresco del resumen: nunca se reemplaza un resumen bueno por vacío. */
export function nextSummary<T>(
  prev: T | null,
  f: Fetched<T>
): { value: T | null; error: string | null } {
  if (f.kind === 'ok') return { value: f.data, error: null };
  return { value: prev, error: failureText(f, SUMMARY_FAIL) };
}

/**
 * Tras un refresco de la lista de asuntos. `null` = nunca se pudo leer. Si falla, se conserva
 * la lista anterior y se avisa; "No hay asuntos abiertos." solo cuando se leyó bien y vino vacía.
 */
export function nextList<T>(
  prev: T | null,
  f: Fetched<T>,
  texts: { kept: string; never: string } = { kept: LIST_FAIL_KEPT, never: LIST_FAIL_NEVER }
): { value: T | null; warning: string | null } {
  if (f.kind === 'ok') return { value: f.data, warning: null };
  return { value: prev, warning: prev ? texts.kept : texts.never };
}

/** Estado de un asunto del cerebro (etapa 0 y etapa 1 de propuestas). */
export const MATTER_STATE: Record<string, string> = {
  new: 'Esperando análisis',
  analyzing: 'Analizando',
  measured: 'Analizado',
  failed: 'No se pudo analizar',
  proposed: 'Con propuesta (espera tu decisión)',
  waiting_draft: 'Esperando el borrador',
  waiting_owner: 'Esperando tu respuesta en la bandeja',
  decided: 'Decidida por vos (sigue abierta en Mercado Libre)',
};

/** Traducción con respaldo: un valor desconocido se muestra como "Otro", nunca su nombre interno. */
export function label(
  map: Record<string, string>,
  key: string | number | null | undefined
): string {
  if (key === null || key === undefined) return OTHER;
  return Object.prototype.hasOwnProperty.call(map, String(key)) ? map[String(key)] : OTHER;
}

/** Nombre de la empresa según la base; si falta, "Empresa <número>". */
export function sellerName(
  sellers: Array<{ id: number; name: string }> | undefined,
  id: number
): string {
  const name = sellers?.find((s) => s.id === id)?.name?.trim();
  return name ? name : `Empresa ${id}`;
}
